import { useEffect, useState, useCallback, useMemo } from "react";
import { Plus, Pencil, Trash2, Search, ChefHat, AlertTriangle, X, ArrowUpDown, ArrowUp, ArrowDown, Tags, Share2, FolderEdit, Printer, FileSpreadsheet, FileText, Image as ImageIcon, Droplets } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError, API } from "@/lib/api";
import { formatDateShort } from "@/lib/format";
import { useI18n } from "@/contexts/I18nContext";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { DishEditor } from "@/components/carta/DishEditor";
import { DishCategoryManager } from "@/components/carta/DishCategoryManager";
import { DishShareDialog } from "@/components/carta/DishShareDialog";

const EMPTY_DISH = {
  name: "",
  category: "entrantes",
  sale_price: "",
  pvp_vat_pct: "10",
  servings: 1,
  notes: "",
  active: true,
  ingredients: [],
};

function fmt(value, currency = "EUR") {
  if (value == null || Number.isNaN(Number(value))) return "—";
  const v = Number(value);
  try {
    return new Intl.NumberFormat("es-ES", { style: "currency", currency, maximumFractionDigits: 2, useGrouping: "always" }).format(v);
  } catch {
    return `${v.toFixed(2)} ${currency}`;
  }
}

export default function Carta() {
  const { lang } = useI18n();
  const { user } = useAuth();
  const baseCurrency = user?.base_currency || "EUR";
  const [dishes, setDishes] = useState([]);
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [q, setQ] = useState("");
  const [filterCategory, setFilterCategory] = useState("all");
  const [filterActive, setFilterActive] = useState("all");
  const [threshold, setThreshold] = useState(35);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null = closed, {} = new, {...} = edit existing
  const [saving, setSaving] = useState(false);
  const [sort, setSort] = useState({ key: "name", dir: "asc" });
  const [showCatManager, setShowCatManager] = useState(false);
  const [sharing, setSharing] = useState(null); // dish being shared
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [bulkCatOpen, setBulkCatOpen] = useState(false);
  const [bulkCat, setBulkCat] = useState("");
  const [bulkShareOpen, setBulkShareOpen] = useState(false);
  const [bulkShareEmail, setBulkShareEmail] = useState("");
  const [bulkBusy, setBulkBusy] = useState(false);
  // iter 82.7n — bulk-merma modal
  const [bulkMermaOpen, setBulkMermaOpen] = useState(false);
  const [bulkMermaCat, setBulkMermaCat] = useState("");
  const [bulkMermaValue, setBulkMermaValue] = useState("10");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [d, p, c] = await Promise.all([
        api.get("/dishes", { params: { threshold } }),
        api.get("/products"),
        api.get("/dish-categories"),
      ]);
      setDishes(d.data);
      setProducts(p.data);
      setCategories(c.data);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  }, [threshold]);

  useEffect(() => { load(); }, [load]);

  const productNames = useMemo(() => products.map((p) => p.name), [products]);
  const productPriceMap = useMemo(() => {
    const m = {};
    for (const p of products) {
      if (p.last_price != null) m[(p.name || "").toLowerCase()] = p.last_price;
    }
    return m;
  }, [products]);

  const visible = useMemo(() => {
    const filtered = dishes.filter((d) => {
      if (q && !d.name.toLowerCase().includes(q.toLowerCase())) return false;
      if (filterCategory !== "all" && d.category !== filterCategory) return false;
      if (filterActive === "active" && !d.active) return false;
      if (filterActive === "inactive" && d.active) return false;
      return true;
    });
    const dir = sort.dir === "asc" ? 1 : -1;
    const get = (d) => {
      switch (sort.key) {
        case "name": return (d.name || "").toLowerCase();
        case "category": return (d.category || "").toLowerCase();
        case "sale_price": return d.sale_price ?? -Infinity;
        case "total_cost": return d.total_cost ?? -Infinity;
        case "food_cost_pct": return d.food_cost_pct == null ? -Infinity : d.food_cost_pct;
        case "margin": return d.margin ?? -Infinity;
        default: return 0;
      }
    };
    return [...filtered].sort((a, b) => {
      const va = get(a), vb = get(b);
      if (va < vb) return -1 * dir;
      if (va > vb) return 1 * dir;
      return 0;
    });
  }, [dishes, q, filterCategory, filterActive, sort]);

  const toggleSort = (key) => {
    setSort((s) => s.key === key
      ? { key, dir: s.dir === "asc" ? "desc" : "asc" }
      : { key, dir: key === "food_cost_pct" || key === "total_cost" ? "desc" : "asc" });
  };

  const renderSortIcon = (k) => {
    if (sort.key !== k) return <ArrowUpDown size={11} className="opacity-40" />;
    return sort.dir === "asc"
      ? <ArrowUp size={11} className="text-[var(--primary)]" />
      : <ArrowDown size={11} className="text-[var(--primary)]" />;
  };

  const alertCount = visible.filter((d) => d.alert).length;
  // iter 82.7n — outlier counts (food cost >40% high / <10% low).
  const outlierHigh = visible.filter((d) => d.outlier_high).length;
  const outlierLow = visible.filter((d) => d.outlier_low).length;

  // Inline-edit merma per dish (iter 82.7n). We keep the draft locally so the
  // user can type freely; commit on blur or Enter.
  const [mermaDraft, setMermaDraft] = useState({}); // {dish_id: "12"}
  const commitMerma = async (dish) => {
    const draftVal = mermaDraft[dish.id];
    if (draftVal === undefined) return;
    const num = parseFloat(draftVal);
    if (Number.isNaN(num) || num < 0 || num > 100) {
      toast.error(lang === "es" ? "Merma debe estar entre 0 y 100" : "Merma must be between 0 and 100");
      setMermaDraft((d) => { const n = { ...d }; delete n[dish.id]; return n; });
      return;
    }
    if (Math.abs(num - (dish.merma_pct || 0)) < 0.01) {
      setMermaDraft((d) => { const n = { ...d }; delete n[dish.id]; return n; });
      return;
    }
    try {
      await api.patch(`/dishes/${dish.id}/merma`, { merma_pct: num });
      toast.success(lang === "es" ? "Merma actualizada" : "Merma updated");
      setMermaDraft((d) => { const n = { ...d }; delete n[dish.id]; return n; });
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  // iter 82.7n — export carta (xlsx/pdf) respecting the currently active
  // category filter and/or multi-selection. Backend endpoint returns a Blob.
  const exportCarta = async (fmt) => {
    try {
      const params = { fmt };
      if (filterCategory && filterCategory !== "all") params.category = filterCategory;
      if (selectedIds.size > 0) params.ids = Array.from(selectedIds).join(",");
      const res = await api.get("/dishes/export", { params, responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      const ext = fmt === "pdf" ? "pdf" : "xlsx";
      const slug = (filterCategory && filterCategory !== "all") ? filterCategory : "carta";
      a.download = `${slug}-${new Date().toISOString().slice(0, 10)}.${ext}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success(lang === "es" ? `Descargado (${fmt.toUpperCase()})` : `Downloaded (${fmt.toUpperCase()})`);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const applyBulkMerma = async () => {
    const cat = (bulkMermaCat || "").trim().toLowerCase();
    const val = parseFloat(bulkMermaValue);
    if (!cat || Number.isNaN(val)) {
      toast.error(lang === "es" ? "Elige categoría y % válidos" : "Pick valid category and %");
      return;
    }
    setBulkBusy(true);
    try {
      const res = await api.post("/dishes/bulk-merma", { category: cat, merma_pct: val });
      toast.success(lang === "es"
        ? `${res.data.affected} plato(s) con merma ${val}%`
        : `${res.data.affected} dish(es) merma=${val}%`);
      setBulkMermaOpen(false);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBulkBusy(false);
    }
  };

  const remove = async (dish) => {
    if (!window.confirm(lang === "es" ? `¿Borrar el plato "${dish.name}"?` : `Delete "${dish.name}"?`)) return;
    try {
      await api.delete(`/dishes/${dish.id}`);
      toast.success(lang === "es" ? "Plato eliminado" : "Dish deleted");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  // ---- Multi-selection helpers ----
  const visibleIds = useMemo(() => visible.map((d) => d.id), [visible]);
  const allVisibleSelected = visibleIds.length > 0 && visibleIds.every((id) => selectedIds.has(id));
  const someVisibleSelected = visibleIds.some((id) => selectedIds.has(id));

  const toggleOne = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const toggleAllVisible = () => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (allVisibleSelected) {
        for (const id of visibleIds) next.delete(id);
      } else {
        for (const id of visibleIds) next.add(id);
      }
      return next;
    });
  };
  const clearSelection = () => setSelectedIds(new Set());

  // Drop selections that no longer exist (after delete/load)
  useEffect(() => {
    setSelectedIds((prev) => {
      if (prev.size === 0) return prev;
      const alive = new Set(dishes.map((d) => d.id));
      let changed = false;
      const next = new Set();
      for (const id of prev) {
        if (alive.has(id)) next.add(id); else changed = true;
      }
      return changed ? next : prev;
    });
  }, [dishes]);

  const bulkDelete = async () => {
    const ids = Array.from(selectedIds);
    if (ids.length === 0) return;
    if (!window.confirm(lang === "es"
      ? `¿Eliminar ${ids.length} plato(s) seleccionado(s)? Esta acción no se puede deshacer.`
      : `Delete ${ids.length} selected dish(es)? This cannot be undone.`)) return;
    setBulkBusy(true);
    try {
      const res = await api.post("/dishes/bulk", { dish_ids: ids, action: "delete" });
      toast.success(lang === "es"
        ? `${res.data.affected} plato(s) eliminado(s)`
        : `${res.data.affected} dish(es) deleted`);
      clearSelection();
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBulkBusy(false);
    }
  };

  const bulkSetCategory = async () => {
    const ids = Array.from(selectedIds);
    const cat = (bulkCat || "").trim().toLowerCase();
    if (ids.length === 0 || !cat) return;
    setBulkBusy(true);
    try {
      const res = await api.post("/dishes/bulk", { dish_ids: ids, action: "set_category", category: cat });
      toast.success(lang === "es"
        ? `${res.data.affected} plato(s) → "${cat}"`
        : `${res.data.affected} dish(es) → "${cat}"`);
      setBulkCatOpen(false);
      setBulkCat("");
      clearSelection();
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBulkBusy(false);
    }
  };

  const bulkShare = async () => {
    const ids = Array.from(selectedIds);
    const email = (bulkShareEmail || "").trim().toLowerCase();
    if (ids.length === 0 || !email) return;
    setBulkBusy(true);
    try {
      const res = await api.post("/dishes/share-bulk", { dish_ids: ids, to_email: email });
      const shared = res.data?.shared_count ?? 0;
      const errs = res.data?.error_count ?? 0;
      if (errs > 0) {
        toast.warning(lang === "es"
          ? `${shared} compartido(s), ${errs} con error → ${res.data.recipient_name || email}`
          : `${shared} shared, ${errs} failed → ${res.data.recipient_name || email}`);
      } else {
        toast.success(lang === "es"
          ? `${shared} plato(s) compartido(s) con ${res.data.recipient_name || email}`
          : `${shared} dish(es) shared with ${res.data.recipient_name || email}`);
      }
      setBulkShareOpen(false);
      setBulkShareEmail("");
      clearSelection();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBulkBusy(false);
    }
  };

  const submit = async (payload) => {
    setSaving(true);
    try {
      const cleaned = {
        ...payload,
        sale_price: parseFloat(payload.sale_price) || 0,
        pvp_vat_pct: payload.pvp_vat_pct === "" || payload.pvp_vat_pct == null
          ? 10.0
          : (parseFloat(payload.pvp_vat_pct) || 0),
        servings: parseFloat(payload.servings) || 1,
        ingredients: payload.ingredients
          .filter((i) => i.product_name && i.qty)
          .map((i) => ({
            product_name: i.product_name,
            qty: parseFloat(i.qty) || 0,
            unit: i.unit || null,
            notes: i.notes || null,
            unit_price_override:
              i.unit_price_override === "" || i.unit_price_override == null
                ? null
                : parseFloat(i.unit_price_override),
            vat_pct:
              i.vat_pct === "" || i.vat_pct == null
                ? 10.0
                : parseFloat(i.vat_pct),
            sub_dish_id: i.sub_dish_id || null,
            price_mode: i.price_mode || null,
          })),
      };
      if (payload.id) {
        await api.put(`/dishes/${payload.id}`, cleaned);
        toast.success(lang === "es" ? "Plato actualizado" : "Dish updated");
      } else {
        await api.post("/dishes", cleaned);
        toast.success(lang === "es" ? "Plato creado" : "Dish created");
      }
      setEditing(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between gap-3 flex-wrap">
        <div>
          <p className="text-xs uppercase tracking-[0.15em] text-[var(--text-muted)] mb-2">
            {lang === "es" ? "Cocina" : "Kitchen"}
          </p>
          <h1 className="font-display text-4xl font-bold tracking-tight leading-none flex items-center gap-2">
            <ChefHat size={32} />
            {lang === "es" ? "Carta y Escandallos" : "Menu & Recipe Costs"}
          </h1>
          <p className="text-sm text-[var(--text-secondary)] mt-2 max-w-2xl">
            {lang === "es"
              ? "Controla el coste real de cada plato con los ingredientes y sus precios actuales (auto-actualizados desde tus facturas)."
              : "Track the real cost of each dish using your latest invoice prices."}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" data-testid="carta-print-trigger">
                <Printer size={14} className="mr-1.5" />
                {lang === "es" ? "Imprimir" : "Print"}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-48">
              <DropdownMenuItem onClick={() => exportCarta("pdf")} data-testid="carta-print-pdf">
                <FileText size={14} className="mr-2" />
                {lang === "es" ? "PDF (cocina/cliente)" : "PDF (kitchen)"}
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => exportCarta("xlsx")} data-testid="carta-print-xlsx">
                <FileSpreadsheet size={14} className="mr-2" />
                {lang === "es" ? "Excel (escandallo)" : "Excel (recipe cost)"}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          <Button
            variant="outline"
            onClick={() => { setBulkMermaCat(filterCategory !== "all" ? filterCategory : ""); setBulkMermaValue("10"); setBulkMermaOpen(true); }}
            data-testid="carta-bulk-merma-trigger"
          >
            <Droplets size={14} className="mr-1.5" />
            {lang === "es" ? "Merma por categoría" : "Bulk merma"}
          </Button>
          <Button
            variant="outline"
            onClick={() => setShowCatManager(true)}
            data-testid="carta-manage-categories"
          >
            <Tags size={14} className="mr-1.5" />
            {lang === "es" ? "Categorías" : "Categories"}
          </Button>
          <Button
            onClick={() => setEditing({ ...EMPTY_DISH, ingredients: [{ product_name: "", qty: "", unit: "", unit_price_override: "", vat_pct: "10" }] })}
            className="bg-[var(--primary)] hover:bg-[var(--primary-hover)] text-white"
            data-testid="carta-new-dish"
          >
            <Plus size={14} className="mr-1.5" />
            {lang === "es" ? "Nuevo plato" : "New dish"}
          </Button>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-[var(--surface)] border border-[var(--border-color)] rounded-lg p-4 grid grid-cols-1 md:grid-cols-4 gap-3">
        <div className="relative md:col-span-2">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--text-muted)]" />
          <Input
            placeholder={lang === "es" ? "Buscar plato..." : "Search dish..."}
            value={q}
            onChange={(e) => setQ(e.target.value)}
            data-testid="carta-search"
            className="pl-9 h-9"
          />
        </div>
        <Select value={filterCategory} onValueChange={setFilterCategory}>
          <SelectTrigger className="h-9" data-testid="carta-filter-category">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{lang === "es" ? "Todas las categorías" : "All categories"}</SelectItem>
            {categories.map((c) => (
              <SelectItem key={c.name} value={c.name}>{c.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={filterActive} onValueChange={setFilterActive}>
          <SelectTrigger className="h-9" data-testid="carta-filter-active">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{lang === "es" ? "Activos e inactivos" : "Active + inactive"}</SelectItem>
            <SelectItem value="active">{lang === "es" ? "Solo en carta" : "On menu only"}</SelectItem>
            <SelectItem value="inactive">{lang === "es" ? "Retirados" : "Removed"}</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="flex items-center gap-3 px-4 py-2.5 rounded-lg bg-[var(--surface)] border border-[var(--border-color)]">
        <Label htmlFor="threshold" className="text-sm">
          {lang === "es" ? "Umbral food cost (%):" : "Food cost threshold (%):"}
        </Label>
        <Input
          id="threshold"
          type="number"
          step="0.5"
          min="0"
          value={threshold}
          onChange={(e) => setThreshold(parseFloat(e.target.value) || 0)}
          data-testid="carta-threshold"
          className="h-8 w-24 font-mono-num"
        />
        <span className="text-xs text-[var(--text-muted)]">
          {lang === "es"
            ? "Los platos por encima se marcan en rojo."
            : "Dishes above this are flagged red."}
        </span>
        {alertCount > 0 && (
          <span className="ml-auto inline-flex items-center gap-1 text-sm text-[var(--status-warning)] font-medium">
            <AlertTriangle size={14} />
            {alertCount} {lang === "es" ? "platos por encima del umbral" : "dishes above threshold"}
          </span>
        )}
      </div>

      {/* iter 82.7n — AVISO GRANDE para outliers de food cost >40% o <10%. */}
      {(outlierHigh + outlierLow) > 0 && (
        <div
          data-testid="carta-outlier-banner"
          className="rounded-lg border-2 border-[var(--status-negative)]/60 bg-[var(--status-negative-bg)] p-4 flex items-start gap-3"
        >
          <AlertTriangle size={28} className="text-[var(--status-negative)] shrink-0 mt-0.5" />
          <div className="flex-1 min-w-0">
            <p className="text-base font-bold text-[var(--status-negative)]">
              {lang === "es" ? "Revisa tus escandallos" : "Review your recipe costs"}
            </p>
            <p className="text-sm text-[var(--text-primary)] mt-1">
              {lang === "es" ? (
                <>
                  {outlierHigh > 0 && <><strong>{outlierHigh}</strong> plato(s) con food cost <strong>&gt; 40%</strong> (demasiado caros).</>}
                  {outlierHigh > 0 && outlierLow > 0 && " "}
                  {outlierLow > 0 && <><strong>{outlierLow}</strong> plato(s) con food cost <strong>&lt; 10%</strong> (sospecha de datos incompletos).</>}
                </>
              ) : (
                <>
                  {outlierHigh > 0 && <><strong>{outlierHigh}</strong> dish(es) with food cost <strong>&gt; 40%</strong>.</>}
                  {outlierHigh > 0 && outlierLow > 0 && " "}
                  {outlierLow > 0 && <><strong>{outlierLow}</strong> dish(es) with food cost <strong>&lt; 10%</strong> (likely missing prices).</>}
                </>
              )}
            </p>
          </div>
        </div>
      )}

      {/* Bulk action bar */}
      {selectedIds.size > 0 && (
        <div
          data-testid="carta-bulk-bar"
          className="flex items-center justify-between gap-3 px-4 py-2.5 rounded-lg bg-[var(--primary)]/10 border border-[var(--primary)]/40"
        >
          <div className="text-sm font-medium" data-testid="carta-bulk-count">
            {selectedIds.size} {lang === "es" ? "plato(s) seleccionado(s)" : "dish(es) selected"}
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => { setBulkCat(""); setBulkCatOpen(true); }}
              disabled={bulkBusy}
              data-testid="carta-bulk-change-category"
            >
              <FolderEdit size={14} className="mr-1.5" />
              {lang === "es" ? "Cambiar categoría" : "Change category"}
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setBulkShareOpen(true)}
              disabled={bulkBusy}
              data-testid="carta-bulk-share"
            >
              <Share2 size={14} className="mr-1.5" />
              {lang === "es" ? "Compartir" : "Share"}
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={bulkDelete}
              disabled={bulkBusy}
              className="text-[var(--status-negative)] border-[var(--status-negative)]/40 hover:bg-[var(--status-negative)]/10"
              data-testid="carta-bulk-delete"
            >
              <Trash2 size={14} className="mr-1.5" />
              {lang === "es" ? "Eliminar" : "Delete"}
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={clearSelection}
              disabled={bulkBusy}
              data-testid="carta-bulk-clear"
            >
              <X size={14} className="mr-1.5" />
              {lang === "es" ? "Limpiar" : "Clear"}
            </Button>
          </div>
        </div>
      )}

      {/* Table */}
      <div className="bg-[var(--surface)] border border-[var(--border-color)] rounded-lg overflow-x-auto">
        <table className="w-full text-sm" data-testid="carta-table">
          <thead className="text-[10px] uppercase tracking-wider text-[var(--text-muted)] bg-[var(--surface-hover)]/40">
            <tr className="border-b border-[var(--border-color)]">
              <th className="py-2.5 px-3 w-10">
                <input
                  type="checkbox"
                  checked={allVisibleSelected}
                  ref={(el) => { if (el) el.indeterminate = !allVisibleSelected && someVisibleSelected; }}
                  onChange={toggleAllVisible}
                  className="rounded border-[var(--border-color)] cursor-pointer"
                  data-testid="carta-select-all"
                  aria-label={lang === "es" ? "Seleccionar todos" : "Select all"}
                />
              </th>
              <th className="text-left py-2.5 px-3">
                <button
                  type="button"
                  onClick={() => toggleSort("name")}
                  className="inline-flex items-center gap-1.5 uppercase tracking-wider hover:text-[var(--text-primary)]"
                  data-testid="sort-name"
                >
                  {lang === "es" ? "Plato" : "Dish"} {renderSortIcon("name")}
                </button>
              </th>
              <th className="text-left py-2.5 px-3">
                <button
                  type="button"
                  onClick={() => toggleSort("category")}
                  className="inline-flex items-center gap-1.5 uppercase tracking-wider hover:text-[var(--text-primary)]"
                  data-testid="sort-category"
                >
                  {lang === "es" ? "Categoría" : "Category"} {renderSortIcon("category")}
                </button>
              </th>
              <th className="text-right py-2.5 px-3">
                <button
                  type="button"
                  onClick={() => toggleSort("sale_price")}
                  className="inline-flex items-center gap-1.5 uppercase tracking-wider hover:text-[var(--text-primary)] ml-auto"
                  data-testid="sort-sale-price"
                >
                  PVP {renderSortIcon("sale_price")}
                </button>
              </th>
              <th className="text-right py-2.5 px-3">
                <button
                  type="button"
                  onClick={() => toggleSort("total_cost")}
                  className="inline-flex items-center gap-1.5 uppercase tracking-wider hover:text-[var(--text-primary)] ml-auto"
                  data-testid="sort-cost"
                >
                  {lang === "es" ? "Coste" : "Cost"} {renderSortIcon("total_cost")}
                </button>
              </th>
              <th className="text-right py-2.5 px-3">
                <button
                  type="button"
                  onClick={() => toggleSort("food_cost_pct")}
                  className="inline-flex items-center gap-1.5 uppercase tracking-wider hover:text-[var(--text-primary)] ml-auto"
                  data-testid="sort-food-cost"
                >
                  Food cost % {renderSortIcon("food_cost_pct")}
                </button>
              </th>
              <th className="text-right py-2.5 px-3">
                <button
                  type="button"
                  onClick={() => toggleSort("margin")}
                  className="inline-flex items-center gap-1.5 uppercase tracking-wider hover:text-[var(--text-primary)] ml-auto"
                  data-testid="sort-margin"
                >
                  {lang === "es" ? "Margen" : "Margin"} {renderSortIcon("margin")}
                </button>
              </th>
              <th
                className="text-right py-2.5 px-3 w-24 uppercase tracking-wider"
                title={lang === "es" ? "Merma % — editable al vuelo" : "Merma % — editable inline"}
              >
                Merma %
              </th>
              <th className="text-center py-2.5 px-3">{lang === "es" ? "En carta" : "On menu"}</th>
              <th className="text-right py-2.5 px-3 whitespace-nowrap" title={lang === "es" ? "Fecha de la última actualización del plato / precios" : "Date of last dish / price update"}>
                {lang === "es" ? "Última act." : "Last update"}
              </th>
              <th className="w-20" />
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan="11" className="text-center py-10 text-[var(--text-muted)]">…</td></tr>
            )}
            {!loading && visible.length === 0 && (
              <tr><td colSpan="11" className="text-center py-16 text-[var(--text-muted)]">
                <ChefHat size={32} className="mx-auto mb-3 opacity-50" />
                {lang === "es"
                  ? "Aún no hay platos. Crea el primero con el botón \"Nuevo plato\"."
                  : "No dishes yet. Create one with the \"New dish\" button."}
              </td></tr>
            )}
            {!loading && visible.map((d) => (
              <tr
                key={d.id}
                className={`border-b border-[var(--border-color)] last:border-0 hover:bg-[var(--surface-hover)] ${
                  d.outlier_high
                    ? "ring-2 ring-inset ring-[var(--status-negative)]/60 bg-[var(--status-negative-bg)]/40"
                    : d.outlier_low
                      ? "ring-2 ring-inset ring-[var(--status-warning)]/60 bg-[var(--status-warning-bg)]/40"
                      : d.alert ? "ring-1 ring-inset ring-[var(--status-warning)]/40" : ""
                } ${selectedIds.has(d.id) ? "bg-[var(--primary)]/5" : ""}`}
                data-testid={`dish-row-${d.id}`}
              >
                <td className="py-2.5 px-3">
                  <input
                    type="checkbox"
                    checked={selectedIds.has(d.id)}
                    onChange={() => toggleOne(d.id)}
                    className="rounded border-[var(--border-color)] cursor-pointer"
                    data-testid={`dish-select-${d.id}`}
                    aria-label={lang === "es" ? `Seleccionar ${d.name}` : `Select ${d.name}`}
                  />
                </td>
                <td className="py-2.5 px-3">
                  <div className="flex items-center gap-2">
                    {d.photo_url ? (
                      <img
                        src={`${API}${d.photo_url}`}
                        alt={d.name}
                        className="w-10 h-10 rounded object-cover border border-[var(--border-color)]"
                        data-testid={`dish-photo-${d.id}`}
                        loading="lazy"
                      />
                    ) : (
                      <div className="w-10 h-10 rounded bg-[var(--surface-hover)] flex items-center justify-center text-[var(--text-muted)] border border-dashed border-[var(--border-color)]">
                        <ImageIcon size={14} />
                      </div>
                    )}
                    <div>
                      <div className="font-medium capitalize">{d.name}</div>
                      <div className="text-[10px] text-[var(--text-muted)]">
                        {d.ingredients.length} {lang === "es" ? "ingredientes" : "ingredients"}
                        {d.ingredients.some((i) => i.missing_price) && (
                          <span className="ml-2 text-amber-500">
                            · {lang === "es" ? "faltan precios" : "missing prices"}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                </td>
                <td className="py-2.5 px-3 capitalize text-[var(--text-secondary)]">{d.category}</td>
                <td className="py-2.5 px-3 text-right font-mono-num">{fmt(d.sale_price, baseCurrency)}</td>
                <td className="py-2.5 px-3 text-right font-mono-num">
                  <div>{fmt(d.total_cost, baseCurrency)}</div>
                  {d.servings > 1 && (
                    <div className="text-[10px] text-[var(--text-muted)]" data-testid={`dish-cost-per-portion-${d.id}`}>
                      {fmt(d.cost_per_portion, baseCurrency)} / {lang === "es" ? "ración" : "portion"}
                    </div>
                  )}
                </td>
                <td className="py-2.5 px-3 text-right">
                  <span
                    data-testid={`dish-fc-${d.id}`}
                    className={`inline-block px-2 py-0.5 rounded font-mono-num text-xs font-semibold ${
                      d.alert
                        ? "bg-[var(--status-negative-bg)] text-[var(--status-negative)]"
                        : d.food_cost_pct == null
                        ? "bg-[var(--surface-hover)] text-[var(--text-muted)]"
                        : "bg-[var(--status-positive-bg)] text-[var(--status-positive)]"
                    }`}
                  >
                    {d.food_cost_pct == null ? "—" : `${d.food_cost_pct.toFixed(1)}%`}
                  </span>
                </td>
                <td className="py-2.5 px-3 text-right font-mono-num">
                  {fmt(d.margin, baseCurrency)}
                  <span className="text-[10px] text-[var(--text-muted)] ml-1">
                    ({d.margin_pct == null ? "—" : `${d.margin_pct.toFixed(1)}%`})
                  </span>
                </td>
                <td className="py-2.5 px-3 text-right">
                  <Input
                    type="number"
                    step="0.5"
                    min="0"
                    max="100"
                    value={mermaDraft[d.id] !== undefined ? mermaDraft[d.id] : (d.merma_pct ?? 10)}
                    onChange={(e) => setMermaDraft((prev) => ({ ...prev, [d.id]: e.target.value }))}
                    onBlur={() => commitMerma(d)}
                    onKeyDown={(e) => { if (e.key === "Enter") { e.target.blur(); } }}
                    className="h-8 w-20 text-right font-mono-num ml-auto"
                    data-testid={`dish-merma-${d.id}`}
                  />
                </td>
                <td className="py-2.5 px-3 text-center">
                  <span className={`inline-block w-2 h-2 rounded-full ${d.active ? "bg-[var(--status-positive)]" : "bg-[var(--text-muted)]"}`} />
                </td>
                <td
                  className="py-2.5 px-3 text-right text-xs text-[var(--text-muted)] font-mono-num whitespace-nowrap"
                  data-testid={`dish-updated-${d.id}`}
                  title={d.updated_at ? new Date(d.updated_at).toLocaleString(lang === "es" ? "es-ES" : "en-US") : ""}
                >
                  {formatDateShort(d.updated_at)}
                </td>
                <td className="py-2.5 px-3 text-right">
                  <button
                    onClick={() => setSharing(d)}
                    className="p-1.5 rounded hover:bg-[var(--surface-hover)] text-[var(--text-secondary)]"
                    title={lang === "es" ? "Compartir" : "Share"}
                    data-testid={`dish-share-${d.id}`}
                  >
                    <Share2 size={14} />
                  </button>
                  <button
                    onClick={() => setEditing({
                      ...d,
                      sale_price: String(d.sale_price),
                      pvp_vat_pct: d.pvp_vat_pct == null ? "10" : String(d.pvp_vat_pct),
                      ingredients: d.ingredients.map((i) => ({
                        ...i,
                        qty: String(i.qty),
                        unit_price_override:
                          i.unit_price_override == null ? "" : String(i.unit_price_override),
                        vat_pct: i.vat_pct == null ? "10" : String(i.vat_pct),
                      })),
                    })}
                    className="p-1.5 rounded hover:bg-[var(--surface-hover)] text-[var(--text-secondary)]"
                    title={lang === "es" ? "Editar" : "Edit"}
                    data-testid={`dish-edit-${d.id}`}
                  >
                    <Pencil size={14} />
                  </button>
                  <button
                    onClick={() => remove(d)}
                    className="p-1.5 rounded hover:bg-[var(--surface-hover)] text-[var(--status-negative)]"
                    title={lang === "es" ? "Borrar" : "Delete"}
                    data-testid={`dish-delete-${d.id}`}
                  >
                    <Trash2 size={14} />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {editing && (
        <DishEditor
          initial={editing}
          categories={categories}
          productNames={productNames}
          productPriceMap={productPriceMap}
          subRecipeDishes={dishes.filter(
            (d) => ["salsas", "salsa", "guarniciones", "guarnicion", "guarniciones y salsas", "producciones", "produccion"]
              .includes((d.category || "").toLowerCase())
              && d.id !== editing.id,
          )}
          baseCurrency={baseCurrency}
          saving={saving}
          onCancel={() => !saving && setEditing(null)}
          onSubmit={submit}
          lang={lang}
          onCategoriesChanged={async () => {
            try {
              const c = await api.get("/dish-categories");
              setCategories(c.data);
            } catch (_) { /* silent */ }
          }}
        />
      )}

      {showCatManager && (
        <DishCategoryManager
          categories={categories}
          onClose={() => setShowCatManager(false)}
          onChanged={load}
          lang={lang}
        />
      )}

      {sharing && (
        <DishShareDialog
          dish={sharing}
          onClose={() => setSharing(null)}
          lang={lang}
        />
      )}

      {bulkCatOpen && (
        <Dialog open onOpenChange={(o) => { if (!o && !bulkBusy) { setBulkCatOpen(false); setBulkCat(""); } }}>
          <DialogContent className="max-w-md" data-testid="carta-bulk-category-dialog">
            <DialogHeader>
              <DialogTitle>
                {lang === "es" ? "Cambiar categoría" : "Change category"}
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-3">
              <p className="text-sm text-[var(--text-secondary)]">
                {lang === "es"
                  ? `Se reasignarán ${selectedIds.size} plato(s) a la categoría elegida.`
                  : `${selectedIds.size} dish(es) will be moved to the chosen category.`}
              </p>
              <div className="space-y-1">
                <Label className="text-xs uppercase tracking-wider">
                  {lang === "es" ? "Categoría" : "Category"}
                </Label>
                <Select value={bulkCat} onValueChange={setBulkCat}>
                  <SelectTrigger data-testid="carta-bulk-category-select">
                    <SelectValue placeholder={lang === "es" ? "Elige una categoría" : "Pick a category"} />
                  </SelectTrigger>
                  <SelectContent>
                    {categories.map((c) => (
                      <SelectItem key={c.name} value={c.name} data-testid={`carta-bulk-category-opt-${c.name}`}>
                        {c.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-[10px] text-[var(--text-muted)]">
                  {lang === "es"
                    ? "¿Necesitas otra categoría? Crea una desde \"Categorías\"."
                    : "Need another category? Create one from \"Categories\"."}
                </p>
              </div>
            </div>
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => { setBulkCatOpen(false); setBulkCat(""); }}
                disabled={bulkBusy}
                data-testid="carta-bulk-category-cancel"
              >
                {lang === "es" ? "Cancelar" : "Cancel"}
              </Button>
              <Button
                onClick={bulkSetCategory}
                disabled={bulkBusy || !bulkCat}
                className="bg-[var(--primary)] hover:bg-[var(--primary-hover)] text-white"
                data-testid="carta-bulk-category-confirm"
              >
                {bulkBusy
                  ? (lang === "es" ? "Aplicando…" : "Applying…")
                  : (lang === "es" ? "Aplicar" : "Apply")}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {bulkShareOpen && (
        <Dialog open onOpenChange={(o) => { if (!o && !bulkBusy) { setBulkShareOpen(false); setBulkShareEmail(""); } }}>
          <DialogContent className="max-w-md" data-testid="carta-bulk-share-dialog">
            <DialogHeader>
              <DialogTitle>
                {lang === "es" ? "Compartir platos" : "Share dishes"}
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-3">
              <p className="text-sm text-[var(--text-secondary)]">
                {lang === "es"
                  ? `Se enviarán ${selectedIds.size} plato(s) al usuario indicado. Los precios de ingredientes se congelan al valor actual para que el destinatario no dependa de tus facturas.`
                  : `${selectedIds.size} dish(es) will be sent to the target user. Ingredient prices are snapshotted so the recipient sees the same food cost.`}
              </p>
              <div className="space-y-1">
                <Label className="text-xs uppercase tracking-wider">
                  {lang === "es" ? "Email del destinatario" : "Recipient email"}
                </Label>
                <Input
                  type="email"
                  value={bulkShareEmail}
                  onChange={(e) => setBulkShareEmail(e.target.value)}
                  placeholder="chef@restaurante.es"
                  autoFocus
                  data-testid="carta-bulk-share-email"
                />
              </div>
            </div>
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => { setBulkShareOpen(false); setBulkShareEmail(""); }}
                disabled={bulkBusy}
                data-testid="carta-bulk-share-cancel"
              >
                {lang === "es" ? "Cancelar" : "Cancel"}
              </Button>
              <Button
                onClick={bulkShare}
                disabled={bulkBusy || !bulkShareEmail.trim()}
                className="bg-[var(--primary)] hover:bg-[var(--primary-hover)] text-white"
                data-testid="carta-bulk-share-confirm"
              >
                <Share2 size={14} className="mr-1.5" />
                {bulkBusy
                  ? (lang === "es" ? "Enviando…" : "Sending…")
                  : (lang === "es" ? "Compartir" : "Share")}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {/* iter 82.7n — Bulk-merma dialog */}
      {bulkMermaOpen && (
        <Dialog open onOpenChange={(o) => { if (!o && !bulkBusy) setBulkMermaOpen(false); }}>
          <DialogContent className="max-w-md" data-testid="carta-bulk-merma-dialog">
            <DialogHeader>
              <DialogTitle>
                {lang === "es" ? "Merma por categoría" : "Bulk merma by category"}
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-3">
              <p className="text-sm text-[var(--text-secondary)]">
                {lang === "es"
                  ? "Aplica el mismo % de merma a todos los platos de una categoría. Útil para corregir márgenes de forma masiva."
                  : "Apply the same merma % to every dish in a category."}
              </p>
              <div className="space-y-1">
                <Label className="text-xs uppercase tracking-wider">
                  {lang === "es" ? "Categoría" : "Category"}
                </Label>
                <Select value={bulkMermaCat} onValueChange={setBulkMermaCat}>
                  <SelectTrigger data-testid="carta-bulk-merma-cat">
                    <SelectValue placeholder={lang === "es" ? "Elige categoría" : "Pick category"} />
                  </SelectTrigger>
                  <SelectContent>
                    {categories.map((c) => (
                      <SelectItem key={c.name} value={c.name}>{c.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label className="text-xs uppercase tracking-wider">
                  {lang === "es" ? "Merma %" : "Merma %"}
                </Label>
                <Input
                  type="number"
                  step="0.5"
                  min="0"
                  max="100"
                  value={bulkMermaValue}
                  onChange={(e) => setBulkMermaValue(e.target.value)}
                  className="h-9 font-mono-num"
                  data-testid="carta-bulk-merma-value"
                />
              </div>
            </div>
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => setBulkMermaOpen(false)}
                disabled={bulkBusy}
              >
                {lang === "es" ? "Cancelar" : "Cancel"}
              </Button>
              <Button
                onClick={applyBulkMerma}
                disabled={bulkBusy || !bulkMermaCat}
                className="bg-[var(--primary)] hover:bg-[var(--primary-hover)] text-white"
                data-testid="carta-bulk-merma-confirm"
              >
                <Droplets size={14} className="mr-1.5" />
                {bulkBusy
                  ? (lang === "es" ? "Aplicando…" : "Applying…")
                  : (lang === "es" ? "Aplicar" : "Apply")}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}

